import React from "react";
import MultiSelectDropdown, { RadioSelectDropdown } from "../../shared/components/multi-select-dropdown/MultiSelectDropdown";
import { makeMultiSelectChangeHandler } from "../../shared/helpers/multiSelectChangeHandler";
import CloseIconRed from "../../assets/icons/close-icon-red.svg";
import errorIcon from "../../assets/icons/error-icon.svg";
import { Controller, useFormContext, Path } from "react-hook-form";
import { FormDataFilterReports, FiltersProps } from "./reports-interface";
import { SelectChangeEvent } from "@mui/material";
import CustomDateRange from "./customDate";
import useFilterLocalStorage from "../../shared/hooks/useFilterLocalStorage";

const Filters = ({
  marketOptions,
  brandOptions,
  consentTypeOptions,
  datePeriodOptions,
}:FiltersProps) => {
  const { control, setValue, watch, trigger, clearErrors } = useFormContext<FormDataFilterReports>();
  const { updateFilter } = useFilterLocalStorage('reports');
  const selectedDatePeriod = watch("datePeriod");
  const currentMarket = watch("market");
  const currentBrands = watch("brands");
  const currentConsent = watch("consentType");

  const clickClearFilters = () => {
    setValue('market', ['All'], { shouldValidate: true });
    setValue('brands', ['All'], { shouldValidate: true });
    setValue('consentType', ['All'], { shouldValidate: true });
    setValue('datePeriod', ["Last Year"], { shouldValidate: true });
    clearErrors(['market','brands','consentType','datePeriod']);
  };

  const clickGenerateReport = async () => {
    const isValid = await trigger();
    if (isValid) {
      updateFilter('market', Array.isArray(currentMarket) ? currentMarket : []);
      updateFilter('brands', Array.isArray(currentBrands) ? currentBrands : []);
      updateFilter('consentType', Array.isArray(currentConsent) ? currentConsent : []);
      const dp = Array.isArray(selectedDatePeriod) ? (selectedDatePeriod[0] ?? "") : (selectedDatePeriod ?? "");
      updateFilter('datePeriod', dp ? [dp] : []);
      setValue('generateReportClicked', true);
    }
  };

  const handleMultiSelectChange = (
    name: Path<FormDataFilterReports>,
    options: readonly string[],
    field: { value: string[]; onChange: (value: string[]) => void }
  ) => {
    const getPrev = () => (Array.isArray(field.value) ? field.value : []);
    const setVals = (vals: string[]) => {
      field.onChange(vals);
      trigger(name);
    };
    return makeMultiSelectChangeHandler(getPrev, setVals, options as string[]);
  };

  return (
    <>
    <div className="reports__container__filters-wrap">
        <div className="reports__container__filters-wrap__filters-row">
          <div className="col-md-9" style={{ display: 'flex', alignItems: 'center', padding: '0' }}>
            <div className="reports__container__filters-wrap__form-control-container-wrap col-md-3">
              <Controller
                control={control}
                name="market"
                rules={{
                  required: "Market is required",
                  validate: value => value.length > 0 || "Market is required" }}
                render={({ field, fieldState: { error } }) => {
                  return(
                  <>
                    <label className="filter-label" htmlFor="market-select">
                      Market
                      <MultiSelectDropdown
                        id="market-select"
                        label="Market"
                        options={marketOptions}
                        selectedValues={field.value}
                        onChange={handleMultiSelectChange("market",marketOptions, field)}
                      />
                    </label>
                      <div className="create-segment__input-wrap__segment-err">&nbsp;
                      {error?.message && (
                        <>
                        <img src={errorIcon} alt="Error Icon" />
                        {error?.message}
                        </>
                        )}
                      </div>
                  </>
                )}}
              />
            </div>
            <div className="reports__container__filters-wrap__form-control-container-wrap col-md-3">
              <Controller
                control={control}
                name="brands"
                rules={{
                  required: "Brand is required",
                  validate: value => value.length > 0 || "Brand is required" }}
                render={({ field, fieldState: { error } }) => {
                  return(
                    <>
                      <label className="filter-label" htmlFor="brands-select">
                        Brand
                        <MultiSelectDropdown
                          id="brands-select"
                          label="Brands"
                          options={brandOptions}
                          selectedValues={field.value}
                          onChange={handleMultiSelectChange("brands",brandOptions, field)}
                        />
                      </label>
                      <div className="create-segment__input-wrap__segment-err">&nbsp;
                      {error?.message && (
                        <>
                        <img src={errorIcon} alt="Error Icon" />
                        {error?.message}
                        </>
                        )}
                      </div>
                    </>
                  )}}
              />
            </div>
            <div className="reports__container__filters-wrap__form-control-container-wrap col-md-3">

              <Controller
                control={control}
                name="consentType"
                rules={{
                  required: "Consent is required",
                  validate: value => value.length > 0 || "Consent is required" }}
                render={({ field, fieldState: { error } }) => {
                  return(
                  <>
                    <label className="filter-label" htmlFor="consentType-select">
                      Consent type
                      <MultiSelectDropdown
                        id="consentType-select"
                        label="Consent Type"
                        options={consentTypeOptions}
                        selectedValues={field.value}
                        onChange={handleMultiSelectChange("consentType",consentTypeOptions, field)}
                      />
                    </label>
                    <div className="create-segment__input-wrap__segment-err">&nbsp;
                    {error?.message && (
                      <>
                      <img src={errorIcon} alt="Error Icon" />
                      {error?.message}
                      </>
                      )}
                    </div>
                  </>
                )}}
              />
            </div>
            <div className="reports__container__filters-wrap__form-control-container-wrap col-md-3">
            <Controller
              control={control}
              name="datePeriod"
              rules={{
                required: "Date period is required",
                validate: value => value.length > 0 || "Date period is required" }}
              render={({ field, fieldState: { error } }) => {
                const valueAsString = Array.isArray(field.value)
                    ? field.value[0] ?? ""
                    : field.value ?? "";
                  const handleChange = (event: SelectChangeEvent<string>) => {
                    const v = event.target.value as string;
                    field.onChange(v);
                    trigger("datePeriod");
                  }
                return(
                <>
                  <label className="filter-label" htmlFor="datePeriod-select">
                    Date period
                    <RadioSelectDropdown
                      id="datePeriod-select"
                      label="Date Period"
                      options={datePeriodOptions}
                      selectedValue={valueAsString}
                      onChange={handleChange}
                    />
                  </label>

                    <div className="create-segment__input-wrap__segment-err">&nbsp;
                    {error?.message && (
                      <>
                      <img src={errorIcon} alt="Error Icon" />
                      {error?.message}
                      </>
                      )}
                    </div>

                </>
              )}}
            />

          </div>

        </div>
          <div className="col-md-3" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="col-md-5" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <label className="filter-label" htmlFor="clear-filters-button">
                <button
                  type="button"
                  id="clear-filters-button"
                  name="clear-filters-button"
                  className="btn-secondary no-border"
                  onClick={clickClearFilters}
                >Clear
                  &nbsp;
                  <img className="btn-secondary__icon" src={CloseIconRed} alt="Clear filters" />
                </button>
              </label>
            </div>
            <div className="col-md-7" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <label className="filter-label" htmlFor="clear-filters-button">
                  <button type="button" className="btn-primary" onClick={clickGenerateReport}>
                    Apply filters
                  </button>
              </label>
            </div>
          </div>
      </div>
        {/* Conditionally render the CustomDateRange component */}
        {(Array.isArray(selectedDatePeriod) ? selectedDatePeriod.includes("Custom") : selectedDatePeriod === "Custom") && (
        <>
        <div className="reports__container__filters-wrap__filters-row custom-date-row">
          <div className="col-md-12">
            <h3>Custom Selected Date Period</h3>
          </div>
        </div>

        <div className="reports__container__filters-wrap__filters-row custom-date-row">
          <div className="col-md-9 custom-date-row">
              <div className="col-md-6 custom-date-row" >
                <CustomDateRange />
            </div>
            <div className="col-md-6">
              {/* <div className="reports__container__btn-wrap__label">Custom Compared Date Period</div>
              <div className="reports__container__btn-wrap__btn">
                <CustomDateRange
                  startDate={startDate}
                  endDate={endDate}
                />
              </div> */}
            </div>
          </div>
          <div className="col-md-3">
            &nbsp;
          </div>
        </div>
        </>
        )}
    </div>
    </>
  );
};

export default Filters;
